import {divIcon, map as createMap, marker, tileLayer} from 'leaflet';

const mapElement = document.querySelector<HTMLElement>('#map');

// Tile server of the OpenStreetMap Foundation,
// see https://operations.osmfoundation.org/policies/tiles/
const tileUrl = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const tileAttribution =
	'&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>-Mitwirkende';
const maxZoom = 19;
const defaultZoom = 13;
const defaultTitle = 'Standort';

type Location = {
	latitude: number;
	longitude: number;
	zoom: number;
	title: string;
};

// Read the location from the markup, so no geocoding service is needed
const readLocation = (element: HTMLElement): Location | undefined => {
	/* eslint-disable unicorn/prefer-number-coercion -- `Number()` coerces a missing attribute (`''`) to `0` instead of `NaN`, which would silently place the map at 0°/0° and pin the zoom to `0`. */
	const latitude = Number.parseFloat(element.dataset.latitude ?? '');
	const longitude = Number.parseFloat(element.dataset.longitude ?? '');
	if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
		return undefined;
	}

	const zoom = Number.parseInt(element.dataset.zoom ?? '', 10);
	/* eslint-enable unicorn/prefer-number-coercion */
	return {
		latitude,
		longitude,
		zoom: Number.isNaN(zoom) ? defaultZoom : zoom,
		title: element.dataset.title ?? defaultTitle,
	};
};

const initializeMap = (element: HTMLElement, location: Location) => {
	const position = {lat: location.latitude, lng: location.longitude};

	// Replace the disclaimer with the map container
	element.classList.remove('not-initialized');
	element.replaceChildren();

	const leafletMap = createMap(element, {
		center: position,
		zoom: location.zoom,
		scrollWheelZoom: false,
	});

	tileLayer(tileUrl, {
		attribution: tileAttribution,
		maxZoom,
	}).addTo(leafletMap);

	// Marker is drawn in CSS, so no additional image assets are needed. The pin
	// lives in a child element, because Leaflet positions the icon itself via
	// `transform` and any rotation on it would spin that offset as well.
	marker(position, {
		title: location.title,
		alt: location.title,
		icon: divIcon({
			className: 'map-marker',
			html: '<span class="map-marker-pin"></span>',
			iconSize: [24, 24],
			iconAnchor: [12, 29],
		}),
	}).addTo(leafletMap);
};

if (mapElement) {
	const location = readLocation(mapElement);
	if (location) {
		mapElement.classList.add('not-initialized');
		mapElement.innerHTML = `
			<div class="map-toggle">
				<button type="button">Karte anzeigen</button>
			</div>
			<div class="map-disclaimer">
				<p>Wir verwenden Karten von OpenStreetMap, um geografische Informationen visuell darzustellen. Mit Klick auf den Button stimmen Sie zu, dass Kartenausschnitte vom Server der OpenStreetMap Foundation geladen werden und dabei Ihre IP-Adresse übermittelt wird.</p>
				<p>Nähere Informationen über die Daten&shy;verarbeitung können Sie der <a href="https://osmfoundation.org/wiki/Privacy_Policy">Datenschutzerklärung der OpenStreetMap Foundation</a> entnehmen.</p>
			</div>
		`;

		const toggle = mapElement.querySelector(':scope .map-toggle button');
		toggle?.addEventListener(
			'click',
			() => {
				initializeMap(mapElement, location);
			},
			{once: true},
		);
	}
}
